import styled, { css } from 'styled-components';
import { THEME } from '@/common/styles/theme';

const panelSurface = css`
	border: 1px solid ${THEME.line};
	border-radius: 18px;
	background: rgba(18, 9, 26, 0.72);
	box-shadow: ${THEME.shadow};
	backdrop-filter: blur(18px);
`;

const chatHiddenScrollbar = css`
	scrollbar-width: none;
	-ms-overflow-style: none;

	&::-webkit-scrollbar {
		display: none;
		width: 0;
		height: 0;
	}
`;

export const ChatView = styled.section`
	flex: 1 1 auto;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
	overscroll-behavior: none;
`;

export const ChatLayout = styled.div`
	flex: 1;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: grid;
	grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
	gap: 12px;
	padding: 12px 16px 14px;
	overflow: hidden;
	align-items: stretch;

	@media (max-width: 1080px) {
		grid-template-columns: minmax(0, 1fr);
		padding: 8px 12px calc(10px + env(safe-area-inset-bottom, 0px));
		gap: 0;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 0;
		gap: 0;
	}
`;

export const ChatMainArea = styled.div`
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 12px;
	overflow: hidden;

	${(props) =>
		props.$profilePanelOpen &&
		css`
			grid-template-columns: minmax(0, 1fr) minmax(200px, 250px);
		`}

	@media (max-width: 1080px) {
		position: relative;
		grid-template-columns: minmax(0, 1fr);
		gap: 0;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		${(props) =>
			props.$mobileHidden &&
			css`
				display: none;
			`}
	}
`;

export const ConversationsListContainer = styled.aside`
	${panelSurface}
	display: flex;
	flex-direction: column;
	height: 100%;
	max-height: 100%;
	min-height: 0;
	overflow: hidden;
	padding: 4px;

	@media (max-width: 1080px) {
		display: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		${(props) =>
			props.$mobileVisible
				? css`
						display: flex;
						border: 0;
						border-radius: 0;
						box-shadow: none;
						background: rgba(14, 7, 20, 0.96);
						padding: 0;
						width: 100%;
						min-width: 0;
					`
				: css`
						display: none;
					`}
	}
`;

export const ChatContainer = styled.section`
	height: 100%;
	max-height: 100%;
	min-height: 0;
	display: grid;
	grid-template-rows: auto minmax(0, 1fr) auto;
	width: 100%;
	overflow: hidden;
	border: 1px solid rgba(246, 142, 255, 0.18);
	border-radius: 18px;
	background:
		radial-gradient(circle at 18% 0%, rgba(255, 77, 242, 0.12), transparent 34%),
		rgba(14, 7, 20, 0.66);
	box-shadow: ${THEME.shadow};
	backdrop-filter: blur(18px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		border-radius: 0;
		border: 0;
		box-shadow: none;
	}
`;

export const MessagesContainer = styled.div`
	overflow: auto;
	min-height: 0;
	padding: 12px 14px;
	display: flex;
	flex-direction: column;
	gap: 10px;
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;
	${chatHiddenScrollbar}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		padding: 8px ${THEME.mobileGutter};
		gap: 6px;
	}
`;

export const ChatMessageBubbleSkeleton = styled.div`
	display: flex;
	width: 100%;
	max-width: ${(props) => props.$width || '58%'};
	align-self: ${(props) => (props.$alignRight ? 'flex-end' : 'flex-start')};
`;

export const ChatComposerSkeleton = styled.div`
	min-height: 64px;
	padding: 12px;
	display: grid;
	grid-template-columns: auto auto minmax(0, 1fr) auto;
	gap: 10px;
	align-items: center;
	border-top: 1px solid rgba(255, 255, 255, 0.06);
	background: rgba(14, 7, 20, 0.92);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 52px;
		padding: 8px ${THEME.mobileGutter};
		gap: 6px;
		border-top: 0;
	}
`;

export const SidebarContainer = styled.aside`
	height: 100%;
	max-height: 100%;
	min-height: 0;
	overflow: auto;
	padding: 14px;
	border: 1px solid rgba(246, 142, 255, 0.16);
	border-radius: 18px;
	background:
		radial-gradient(circle at 25% 0%, rgba(255, 77, 242, 0.16), transparent 34%),
		rgba(12, 5, 17, 0.58);
	box-shadow: ${THEME.shadow};
	backdrop-filter: blur(18px);
	overscroll-behavior: contain;
	-webkit-overflow-scrolling: touch;
	${chatHiddenScrollbar}

	@media (max-width: 1080px) {
		position: absolute;
		top: 8px;
		right: 12px;
		bottom: calc(10px + env(safe-area-inset-bottom, 0px));
		height: auto;
		max-height: none;
		z-index: 6;
		width: min(270px, calc(100% - ${THEME.mobileGutter} * 2));
		max-width: calc(100% - ${THEME.mobileGutter} * 2);
		opacity: 0;
		pointer-events: none;
		transform: translateX(calc(100% + ${THEME.mobileGutter}));
		transition: transform 220ms ease, opacity 220ms ease;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		top: 0;
		right: 0;
		bottom: 0;
		width: min(280px, 88vw);
		max-width: 88vw;
		padding: 12px;
		border-radius: 0;
		transform: translateX(100%);
	}

	${(props) =>
		props.$open &&
		css`
			@media (max-width: 1080px) {
				opacity: 1;
				pointer-events: auto;
				transform: translateX(0);
			}
		`}

	@media (min-width: 1081px) {
		position: static;
		opacity: 1;
		pointer-events: auto;
		transform: none;
		width: auto;
		max-width: none;

		${(props) =>
			!props.$open &&
			css`
				display: none;
			`}
	}
`;

export const Composer = styled.div`
	position: relative;
	min-height: 64px;
	padding: 12px;
	display: grid;
	grid-template-columns: ${(props) =>
		props.$voiceCallActive ? '1fr' : 'auto auto minmax(0, 1fr) auto'};
	gap: 10px;
	align-items: center;
	background: ${(props) =>
		props.$voiceCallActive
			? 'rgba(18, 44, 32, 0.28)'
			: 'rgba(255, 255, 255, 0.035)'};
	backdrop-filter: blur(18px);

	@media (max-width: ${THEME.mobileBreakpoint}) {
		grid-template-columns: auto auto minmax(0, 1fr) auto;
		min-height: 52px;
		padding: 8px ${THEME.mobileGutter};
		gap: 6px;
		border-top: 0;
		background: rgba(14, 7, 20, 0.92);
	}
`;

export const ComposerActionButton = styled.button`
	min-height: 42px;
	padding: 0 14px;
	border: 1px solid rgba(255, 214, 255, 0.18);
	border-radius: 999px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	color: rgba(255, 232, 255, 0.92);
	font-size: 0.82rem;
	font-weight: 800;
	background:
		radial-gradient(circle at 96% 50%, rgba(255, 178, 108, 0.18), transparent 38%),
		linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.018)),
		rgba(20, 8, 28, 0.52);
	box-shadow: inset 0 0 12px rgba(255, 255, 255, 0.025), 0 0 14px rgba(255, 77, 242, 0.08);
	cursor: pointer;
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease,
		transform 160ms ease;

	&:hover,
	&:focus-visible {
		border-color: rgba(255, 119, 244, 0.36);
		box-shadow: 0 0 18px rgba(255, 77, 242, 0.16);
		transform: translateY(-1px);
	}

	${(props) =>
		props.$active &&
		css`
			border-color: rgba(255, 119, 244, 0.36);
			background:
				radial-gradient(circle at 96% 50%, rgba(255, 178, 108, 0.24), transparent 38%),
				linear-gradient(135deg, rgba(255, 77, 242, 0.15), rgba(255, 255, 255, 0.035)),
				rgba(25, 9, 34, 0.62);
		`}

	${(props) =>
		props.$iconOnly &&
		css`
			width: 42px;
			min-width: 42px;
			padding: 0;

			@media (max-width: ${THEME.mobileBreakpoint}) {
				width: 36px;
				min-width: 36px;
				min-height: 36px;
				border: 0;
				box-shadow: none;
			}
		`}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
		transform: none;
		box-shadow: none;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		border: 0;
		box-shadow: none;
		background: rgba(255, 255, 255, 0.06);

		&:hover,
		&:focus-visible {
			border: 0;
			box-shadow: none;
			transform: none;
		}
	}

`;

export const ComposerInput = styled.input`
	min-height: 48px;
	border-radius: 999px;
	border: 1px solid rgba(255, 255, 255, 0.09);
	padding: 0 16px;
	color: ${THEME.text};
	font-size: 0.94rem;
	background: rgba(0, 0, 0, 0.24);
	-webkit-user-select: text;
	user-select: text;
	touch-action: manipulation;

	&::placeholder {
		color: rgba(216, 189, 228, 0.72);
	}

	&:focus {
		outline: none;
		border-color: rgba(255, 119, 244, 0.34);
		box-shadow: 0 0 14px rgba(255, 77, 242, 0.12);
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		min-height: 40px;
		padding: 0 12px;
		font-size: 16px;
		border: 0;
		background: rgba(0, 0, 0, 0.32);
		transform: translateZ(0);

		&:focus {
			border: 0;
			box-shadow: none;
		}
	}
`;

export const SendActionButton = styled.button`
	width: 48px;
	min-width: 48px;
	min-height: 48px;
	border-radius: 15px;
	padding: 0;
	border: 1px solid rgba(255, 214, 255, 0.22);
	color: rgba(255, 245, 255, 0.94);
	background:
		radial-gradient(circle at 92% 12%, rgba(255, 178, 108, 0.34), transparent 38%),
		linear-gradient(135deg, rgba(176, 38, 255, 0.74), rgba(255, 77, 242, 0.54));
	box-shadow: inset 0 0 14px rgba(255, 255, 255, 0.055), 0 0 18px rgba(255, 77, 242, 0.2);
	cursor: pointer;
	display: grid;
	place-items: center;
	transition:
		border-color 160ms ease,
		box-shadow 160ms ease,
		transform 160ms ease;

	&:hover:not(:disabled),
	&:focus-visible:not(:disabled) {
		border-color: rgba(255, 214, 255, 0.46);
		box-shadow: inset 0 0 16px rgba(255, 255, 255, 0.07), 0 0 24px rgba(255, 77, 242, 0.28);
		transform: translateY(-1px);
	}

	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}

	${(props) =>
		props.$hidden &&
		css`
			display: none;
		`}

	svg {
		width: 22px;
		height: 22px;
		fill: none;
		stroke: currentColor;
		stroke-width: 2;
		stroke-linecap: round;
		stroke-linejoin: round;
		transform: translate(-1px, 1px);
	}

	@media (max-width: ${THEME.mobileBreakpoint}) {
		width: 40px;
		min-width: 40px;
		min-height: 40px;
		border-radius: 12px;
		border: 0;
		box-shadow: none;

		&:hover:not(:disabled),
		&:focus-visible:not(:disabled) {
			border: 0;
			box-shadow: none;
			transform: none;
		}

		svg {
			width: 20px;
			height: 20px;
		}
	}
`;
